import { useEffect, useRef, useState } from 'react'
import { IconChevronDown, IconCheck } from '@/components/icons/Icons'

/*
  Filtro de seleccion multiple.

  Un `<select>` normal obliga a elegir una sola cosa, y filtrar un tablero casi
  nunca es eso: uno quiere ver "pendiente Y en progreso", o dos etiquetas a la
  vez. `<select multiple>` existe, pero pide Ctrl+clic y no se ve lo elegido sin
  desplegar, asi que aqui va un desplegable con casillas.

  Sin seleccion = sin filtro (todo pasa), que es lo que espera quien no toco
  nada todavia.
*/

export interface MultiOption {
  value: string
  label: string
}

interface Props {
  /** Texto cuando no hay nada elegido. Tambien es la etiqueta accesible. */
  placeholder: string
  options: MultiOption[]
  value: string[]
  onChange: (value: string[]) => void
  /** Ancho minimo del boton, para que la barra de filtros no baile. */
  minWidth?: number
}

export function MultiSelect({ placeholder, options, value, onChange, minWidth = 150 }: Props) {
  const [abierto, setAbierto] = useState(false)
  const caja = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!abierto) return
    const fuera = (e: MouseEvent) => {
      if (caja.current && !caja.current.contains(e.target as Node)) setAbierto(false)
    }
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setAbierto(false) }
    document.addEventListener('mousedown', fuera)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('mousedown', fuera)
      document.removeEventListener('keydown', esc)
    }
  }, [abierto])

  function alternar(v: string) {
    onChange(value.includes(v) ? value.filter(x => x !== v) : [...value, v])
  }

  // Con una sola opcion elegida se muestra su nombre: leer "1 seleccionado"
  // obliga a abrir el desplegable para saber cual es.
  const texto =
    value.length === 0 ? placeholder
      : value.length === 1 ? (options.find(o => o.value === value[0])?.label ?? value[0])
        : `${value.length} seleccionados`

  return (
    <div className="multiselect" ref={caja}>
      <button
        type="button"
        className={`multiselect__boton${value.length > 0 ? ' multiselect__boton--activo' : ''}`}
        style={{ minWidth }}
        onClick={() => setAbierto(v => !v)}
        aria-haspopup="listbox"
        aria-expanded={abierto}
        aria-label={placeholder}
        title={value.length > 1 ? value.map(v => options.find(o => o.value === v)?.label ?? v).join(', ') : undefined}
      >
        <span className="multiselect__texto">{texto}</span>
        {value.length > 1 && <span className="multiselect__contador">{value.length}</span>}
        <IconChevronDown size={13} />
      </button>

      {abierto && (
        <div className="multiselect__panel" role="listbox" aria-multiselectable="true">
          {options.length === 0 ? (
            <p className="multiselect__vacio">No hay opciones.</p>
          ) : (
            // Un <button role="option"> y no un <label> con <input> dentro: esa
            // combinacion dispara el evento dos veces en algunos navegadores
            // (el label reenvia el clic al input), y la opcion quedaba como
            // estaba. Aqui el estado lo lleva React y la casilla es solo visual.
            options.map(o => {
              const marcada = value.includes(o.value)
              return (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={marcada}
                  className={`multiselect__opcion${marcada ? ' multiselect__opcion--marcada' : ''}`}
                  onClick={() => alternar(o.value)}
                >
                  <span className={`multiselect__casilla${marcada ? ' multiselect__casilla--marcada' : ''}`} aria-hidden="true">
                    {marcada && <IconCheck size={11} />}
                  </span>
                  <span className="multiselect__etiqueta">{o.label}</span>
                </button>
              )
            })
          )}
          {value.length > 0 && (
            <button type="button" className="multiselect__limpiar" onClick={() => onChange([])}>
              Quitar seleccion
            </button>
          )}
        </div>
      )}
    </div>
  )
}
